import { SITE } from '~/data/site';

/**
 * Who this site is. The answer is always New Leaf Cleaning.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE IS 60 LINES INSTEAD OF 314
 * ---------------------------------------------------------------------------
 * On the platform, this module resolved the company PER REQUEST. It had to:
 * one Worker served `nlcstpete.com` AND every tenant's `*.broomstack.app`
 * console off the same route table, so a marketing page could not know at build
 * time whose domain it was being rendered on. Getting that wrong put New Leaf's
 * name, phone number and TCPA consent line in front of another company's
 * visitors — which is exactly what happened, and what the 314 lines existed to
 * stop.
 *
 * That question does not exist here. This Worker serves one domain for one
 * company. Every request is New Leaf's, so the whole apparatus — the
 * `/api/branding` round-trip in the root loader, `NLC_HOSTS`, `isNlcOwnHost`,
 * the per-tenant theme override, the "unknown host degrades to neutral copy"
 * fallback, and the `isNewLeaf` gate on roughly forty pieces of content —
 * collapses into a constant.
 *
 * The shape is kept IDENTICAL to the platform's `ResolvedBranding` on purpose.
 * Twenty-one call sites across the ported routes, nav and footer read
 * `useBranding()` / `brandingFromMatches()`, and preserving the interface means
 * this split changes zero lines in any of them. `isNewLeaf` is still here and
 * still `true`; the gates it guards now simply never take the other branch.
 * Deleting the flag would mean editing every one of those call sites inside a
 * change whose gate is page-by-page parity — a bad trade. It can go later, as
 * its own mechanical pass, once parity is proven.
 */

/**
 * New Leaf's palette. Inlined rather than imported from a `lib/theme` module:
 * on the platform that file was 174 lines of per-tenant colour resolution and
 * hex validation, needed because a tenant's colours arrived from the database
 * and were interpolated into a <style> block. Nothing here is dynamic and
 * nothing is interpolated, so there is nothing to validate.
 */
export interface TenantTheme {
  primary: string;
  secondary: string;
  accent: string;
  /** True when these are the company's own choices rather than the platform's. */
  isCustom: boolean;
}

export interface ResolvedBranding {
  id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  phoneHref: string | null;
  isNewLeaf: boolean;
  iconHref: string;
  appleIconHref: string;
  logoUrl: string | null;
  navLinks: ReadonlyArray<{ label: string; href: string }>;
  theme: TenantTheme;
  origin: string;
}

/**
 * New Leaf's own identity, resolved once at build time.
 *
 * `theme` carries New Leaf's real palette with `isCustom: false`, which is what
 * the platform resolved for New Leaf too: the colours live in `app.css` and
 * `marketing.css` as the DEFAULTS, and the platform only ever emitted an
 * override <style> block for tenants who were NOT New Leaf. So there is nothing
 * to override here, and root.tsx emits no theme block at all.
 */
export const BRANDING: ResolvedBranding = {
  id: 'nlc',
  name: SITE.name,
  email: SITE.contact.email,
  phone: null,
  phoneHref: null,
  isNewLeaf: true,
  iconHref: '/images/favicon-32x32.png',
  appleIconHref: '/images/apple-touch-icon.png',
  logoUrl: '/images/nlc-logo.png',
  navLinks: SITE.navLinks,
  theme: { primary: '#4A7C59', secondary: '#D4B896', accent: '#C5D8C7', isCustom: true },
  origin: SITE.url,
};

/** The company this page belongs to. Always New Leaf — see the note above. */
export function useBranding(): ResolvedBranding {
  return BRANDING;
}

/**
 * Same answer, for `meta()` functions.
 *
 * On the platform this dug the root loader's payload out of the route matches,
 * because `meta()` runs without hooks. It takes no argument here — but the
 * signature stays tolerant of one so the ported `meta({ matches })` call sites
 * compile unchanged.
 */
export function brandingFromMatches(_matches?: unknown): ResolvedBranding {
  return BRANDING;
}
